/**
 * TeamMembersPanel — reusable admin surface for `/api/{module}/team` CRUD.
 *
 * Backed by `services/team_router_factory.py` on the server. Same UX as
 * StudioTeam.jsx but parameterised on:
 *   • apiPath   — base URL segment (e.g. "bookshop", "fwtv")
 *   • roles     — [{value, label, description}]
 *   • title     — page heading
 *   • description
 *
 * Add-member flow keeps it simple: email + name + role + password (new
 * user) or email + role (existing admin). Duplicate → 409 handled inline.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Users, UserPlus, Shield, Trash2, RefreshCw } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });


export function TeamMembersPanel({ apiPath, roles, title, description }) {
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const rolesByValue = useMemo(
    () => Object.fromEntries((roles || []).map(r => [r.value, r])),
    [roles],
  );

  const load = async () => {
    setLoading(true);
    try {
      const [list, me] = await Promise.all([
        axios.get(`${API}/api/${apiPath}/team`, { headers: authH() }),
        axios.get(`${API}/api/${apiPath}/team/me`, { headers: authH() })
          .catch(() => ({ data: null })),
      ]);
      setRows(list.data || []);
      setMe(me.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [apiPath]);

  const isSuper = me?.is_super === true;
  const adminRoleValue = roles?.find(r => r.is_admin_role)?.value;
  const canManage = isSuper || (me?.role === adminRoleValue);

  const updateRole = async (row, newRole) => {
    setSavingId(row.id);
    try {
      const r = await axios.put(
        `${API}/api/${apiPath}/team/${row.id}`,
        { role: newRole }, { headers: authH() },
      );
      setRows(rs => rs.map(x => x.id === row.id ? r.data : x));
      toast.success(`Role → ${newRole}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (row) => {
    setSavingId(row.id);
    try {
      const r = await axios.put(
        `${API}/api/${apiPath}/team/${row.id}`,
        { is_active: !row.is_active }, { headers: authH() },
      );
      setRows(rs => rs.map(x => x.id === row.id ? r.data : x));
      toast.success(row.is_active ? 'Deactivated' : 'Activated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (row) => {
    if (!confirm(`Remove ${row.email} from the team? (soft delete — can be re-added later)`)) return;
    setSavingId(row.id);
    try {
      await axios.delete(`${API}/api/${apiPath}/team/${row.id}`, { headers: authH() });
      setRows(rs => rs.filter(x => x.id !== row.id));
      toast.success('Removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid={`team-panel-${apiPath}`}>
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100 inline-flex items-center gap-2">
            <Users className="h-6 w-6 text-indigo-400" /> {title}
          </h1>
          <p className="text-sm text-slate-400 mt-1">{description}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    data-testid="team-refresh-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {canManage && (
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="team-add-btn">
                  <UserPlus className="h-4 w-4 mr-1" /> Add member
                </Button>
              </DialogTrigger>
              <AddMemberDialog
                apiPath={apiPath}
                roles={roles}
                onClose={() => setShowAdd(false)}
                onAdded={(row) => { setRows(rs => [row, ...rs]); setShowAdd(false); }}
              />
            </Dialog>
          )}
        </div>
      </header>

      {me && (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-3 text-xs text-slate-300 flex items-center gap-2">
            <Shield className="h-4 w-4 text-emerald-300" />
            You are <strong className="text-slate-100">{isSuper ? 'Super Admin' : (rolesByValue[me.role]?.label || me.role)}</strong>
            {canManage
              ? <span className="text-emerald-300">— can manage members</span>
              : <span className="text-slate-400">— read only</span>}
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm">Team members ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && (
            <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
          )}
          {!loading && !rows.length && (
            <div className="text-sm text-slate-500 py-6 text-center">
              No team members yet. {canManage && 'Click "Add member" to get started.'}
            </div>
          )}
          {rows.map(row => (
            <div key={row.id}
                  className={`border rounded-lg p-3 flex items-center gap-3 ${
                    row.is_active ? 'border-slate-800 bg-slate-800/30' : 'border-slate-800 bg-slate-900/40 opacity-60'
                  }`}
                  data-testid={`team-row-${row.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-100 font-medium truncate">
                  {row.name || row.email}
                </div>
                <div className="text-xs text-slate-500 truncate">{row.email}</div>
                {row.permissions?.length > 0 && (
                  <div className="text-[10px] text-slate-500 mt-1 truncate">
                    {row.permissions.slice(0, 3).join(' · ')}
                    {row.permissions.length > 3 && ` · +${row.permissions.length - 3}`}
                  </div>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {canManage ? (
                  <Select
                    value={row.role}
                    onValueChange={(v) => updateRole(row, v)}
                    disabled={savingId === row.id}
                  >
                    <SelectTrigger className="h-8 w-40 bg-slate-950 border-slate-700 text-xs"
                                   data-testid={`role-select-${row.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className="border-slate-700 text-slate-300">
                    {rolesByValue[row.role]?.label || row.role}
                  </Badge>
                )}
                {canManage && (
                  <>
                    <Button size="sm" variant="outline"
                             className="h-8 border-slate-700 text-slate-300 hover:bg-slate-800"
                             onClick={() => toggleActive(row)}
                             disabled={savingId === row.id}
                             data-testid={`toggle-active-${row.id}`}>
                      {row.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="destructive"
                             className="h-8"
                             onClick={() => remove(row)}
                             disabled={savingId === row.id}
                             data-testid={`remove-${row.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}


function AddMemberDialog({ apiPath, roles, onClose, onAdded }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(
    roles?.find((r) => r.is_default)?.value || roles?.[0]?.value || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email.trim()) { toast.error('Email required'); return; }
    if (!role) { toast.error('Pick a role first'); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/${apiPath}/team`,
        { email: email.trim(), name: name.trim() || undefined, role,
           password: password.trim() || undefined },
        { headers: authH() },
      );
      toast.success('Added');
      onAdded(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-slate-950 border-slate-800 text-slate-200">
      <DialogHeader>
        <DialogTitle>Add team member</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400">Email</Label>
          <Input value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="teammate@example.com"
                  className="bg-slate-900 border-slate-800"
                  data-testid="add-email" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Name (optional if new user)</Label>
          <Input value={name} onChange={e => setName(e.target.value)}
                  className="bg-slate-900 border-slate-800"
                  data-testid="add-name" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="add-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roles.map(r => (
                <SelectItem key={r.value} value={r.value}>
                  <div className="flex flex-col">
                    <span>{r.label}</span>
                    {r.description && <span className="text-[10px] text-slate-500">{r.description}</span>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Password <span className="opacity-60">— only if the email isn't an existing admin</span></Label>
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  className="bg-slate-900 border-slate-800"
                  data-testid="add-password" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}
                 className="border-slate-700 text-slate-300 hover:bg-slate-800"
                 data-testid="add-cancel">Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="add-submit">
          {busy ? 'Adding…' : 'Add member'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export default TeamMembersPanel;
